import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import WellnessSearchInterface from './components/WellnessSearchInterface';
import FeaturedCenters from './components/FeaturedCenters';
import WellnessStats from './components/WellnessStats';
import PractitionerCarousel from './components/PractitionerCarousel';
import CategoryTiles from './components/CategoryTiles';
import Footer from './components/Footer';

const WellnessRetreatHomepage = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 30 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6 font-heading">
              Discover Your Perfect
              <span className="text-transparent bg-clip-text bg-gradient-to-r text-primary from-primary to-secondary block">
                Wellness Retreat
              </span>
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Find certified EES centers, meditation retreats, and spa resorts for your healing journey. 
              Experience energy enhancement, chakra balancing, and holistic wellness programs worldwide.
            </p>
          </motion.div>

          {/* Hero Search Interface */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: isLoaded ? 1 : 0, scale: isLoaded ? 1 : 0.95 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <WellnessSearchInterface />
          </motion.div>

          {/* Quick Category Tiles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <CategoryTiles />
          </motion.div>
        </div>
      </section>

      {/* Featured Wellness Centers */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4 font-heading">
              Featured Wellness Centers
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Discover top-rated EES centers and wellness retreats trusted by our community
            </p>
          </motion.div>
          <FeaturedCenters />
        </div>
      </section>

      {/* Wellness Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto">
          <WellnessStats />
        </div>
      </section>

      {/* Featured Practitioners */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4 font-heading">
              Certified Wellness Practitioners
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Connect with experienced healers and certified EES practitioners
            </p>
          </motion.div>
          <PractitionerCarousel />
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary to-secondary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 font-heading">
              Ready to Begin Your Wellness Journey?
            </h2>
            <p className="text-xl mb-8">
              Join thousands of people who have discovered transformation through our wellness network
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/wellness-center-listings"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary 
                         font-semibold rounded-lg transition-all duration-200 
                         micro-interaction shadow-elevation-2"
              >
                <Icon name="Search" size={20} className="mr-2" />
                Find Your Retreat
              </Link>
              <Link
                to="/become-partner"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent 
                         border-2 border-white text-white font-semibold rounded-lg 
                         hover:bg-white hover:text-primary transition-all duration-200 
                         micro-interaction"
              >
                <Icon name="Plus" size={20} className="mr-2" />
                List Your Center
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WellnessRetreatHomepage;